import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FileText, Download } from 'lucide-react'
import * as pdfjsLib from 'pdfjs-dist'

// Solo per l'attributo `type` del download — il browser usa comunque
// l'estensione nel nome file per decidere come aprirlo.
const MIME_TYPES = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  csv: 'text/csv',
  txt: 'text/plain',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
}

// Banner di download per l'allegato originale quando non è un PDF (quindi
// non renderizzabile da pdf.js) — es. Excel/Word/CSV. Il testo già parsato
// resta visibile sotto come anteprima; questo dà accesso al file vero,
// nel suo formato originale, per chi vuole aprirlo nel programma nativo.
function OriginalFileBanner({ file }) {
  if (!file) return null
  const mime = MIME_TYPES[file.format] || 'application/octet-stream'
  const href = `data:${mime};base64,${file.base64}`
  return (
    <a href={href} download={file.filename} style={s.downloadBanner}>
      <Download size={15} />
      <span>Scarica l'allegato originale ({file.filename})</span>
    </a>
  )
}

// Worker da CDN — necessario per pdf.js
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'

// Il parser (Core) converte allegati Excel/CSV in testo con una tabella in
// stile markdown (riga di intestazione con "|", riga di separatore fatta di
// "---"). Qui la individuiamo e la teniamo distinta dal resto del testo
// (corpo mail), così può essere renderizzata come vera <table> invece che
// come testo grezzo — vedi isSeparatorRow/splitRow/parseTextWithTables.
function isSeparatorRow(line) {
  const trimmed = line.trim()
  if (!trimmed.includes('|') && !trimmed.includes('-')) return false
  const cells = trimmed.replace(/^\||\|$/g, '').split('|')
  if (cells.length < 2) return false
  return cells.every((c) => /^:?-{2,}:?$/.test(c.trim()))
}

function splitRow(line) {
  let trimmed = line.trim()
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1)
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1)
  return trimmed.split('|').map((c) => c.trim())
}

function parseTextWithTables(text) {
  if (!text) return []
  const lines = text.split('\n')
  const blocks = []
  let textBuffer = []

  const flushText = () => {
    if (textBuffer.length) {
      blocks.push({ type: 'text', content: textBuffer.join('\n') })
      textBuffer = []
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const next = lines[i + 1]
    const looksLikeHeader = line.includes('|') && line.trim() !== ''
    if (looksLikeHeader && next != null && isSeparatorRow(next)) {
      flushText()
      const headers = splitRow(line)
      let j = i + 2
      const rows = []
      while (j < lines.length && lines[j].includes('|') && lines[j].trim() !== '') {
        rows.push(splitRow(lines[j]))
        j++
      }
      blocks.push({ type: 'table', headers, rows })
      i = j - 1
    } else {
      textBuffer.push(line)
    }
  }
  flushText()
  return blocks
}

// Cerca `query` come sottostringa (case-insensitive) dentro `content` e, se
// la trova E non è già stata trovata un'altra occorrenza prima nello stesso
// passaggio di rendering (vedi `matchState`, condiviso da tutta
// TextWithTables), restituisce un array di nodi con la porzione trovata
// avvolta in <mark> (con ref per lo scroll). Altrimenti restituisce il testo
// invariato. Si evidenzia sempre e solo la PRIMA occorrenza nell'intero
// documento, per avere un unico bersaglio verso cui scorrere.
function highlightIfMatch(content, query, matchState, key) {
  if (!query || matchState.found || !content) return content
  const idx = content.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return content
  matchState.found = true
  const before = content.slice(0, idx)
  const match = content.slice(idx, idx + query.length)
  const after = content.slice(idx + query.length)
  return [
    before,
    <mark
      key={`${key}-hl`}
      ref={(el) => { matchState.ref.current = el }}
      style={s.highlight}
    >
      {match}
    </mark>,
    after,
  ]
}

/**
 * Renderizza testo grezzo intervallato da eventuali tabelle in stile
 * markdown come vere <table> HTML, mantenendo il resto (es. corpo della
 * mail) come testo normale.
 *
 * `highlightQuery` (opzionale): testo da cercare e cerchiare (bordo verde)
 * nel documento mentre il puntatore è sopra la riga ordine corrispondente
 * (vedi OrderReview.jsx `onLineHover`) — nessuno scroll automatico, solo
 * riferimento visivo, e sparisce quando il puntatore esce dalla riga.
 * `scrollNonce` (opzionale): cambia ogni volta che l'utente fa doppio click
 * sulla riga ordine (`onLineActivate`) — quando cambia, se c'è un match
 * attualmente cerchiato lo scorre in cima al pannello. Tenuto separato da
 * `highlightQuery` apposta: cerchiare (hover, continuo) e scorrere (doppio
 * click, esplicito) sono due azioni distinte, vedi richiesta utente.
 * Matching euristico (sottostringa nel testo grezzo, nessuna posizione
 * salvata da parser/LLM/Matcher). Fase attuale: solo allegati testo (questo
 * componente) — i PDF veri hanno la stessa interazione in `findTextMatch`.
 */
function TextWithTables({ text, highlightQuery, scrollNonce }) {
  const blocks = parseTextWithTables(text)
  const highlightRef = useRef(null)
  // Reimpostato a ogni render: garantisce che si evidenzi una sola
  // occorrenza per passaggio, la prima incontrata nell'ordine del documento.
  const matchState = { found: false, ref: highlightRef }

  // Scroll solo su richiesta esplicita (doppio click, non hover) — usa
  // qualunque match sia attualmente cerchiato (highlightRef), popolato dal
  // render sopra in base a `highlightQuery`.
  useEffect(() => {
    if (scrollNonce && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollNonce])

  return (
    <>
      {blocks.map((block, i) => {
        if (block.type === 'table') {
          return (
            <div key={i} style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {block.headers.map((h, hi) => (
                      <th key={hi} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <td key={ci} style={s.td}>
                          {highlightIfMatch(cell, highlightQuery, matchState, `${i}-${ri}-${ci}`)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
        if (!block.content.trim()) return null
        return (
          <pre key={i} style={s.preText}>
            {highlightIfMatch(block.content.trim(), highlightQuery, matchState, `${i}`)}
          </pre>
        )
      })}
    </>
  )
}

// Scala usata per renderizzare/posizionare il testo di una pagina alla
// larghezza del container — la stessa usata da PageCanvas per il canvas
// "a schermo" (senza il qualityMultiplier, che serve solo alla nitidezza del
// bitmap, non alla dimensione CSS visualizzata: canvas.style.width:'100%' +
// height:'auto' fa sì che la dimensione mostrata segua sempre questa scala).
function computeBaseScale(page, containerWidth) {
  const naturalViewport = page.getViewport({ scale: 1 })
  return containerWidth / naturalViewport.width
}

// Cerca `query` (sottostringa, case-insensitive) nel testo di un vero PDF,
// scorrendo le pagine in ordine e concatenando gli item di getTextContent()
// (pdf.js spezzetta il testo in tanti piccoli item senza spazi tra loro,
// quindi li uniamo con uno spazio per poter cercare frasi che nella pagina
// attraversano più item). Stessa euristica "sottostringa nel testo grezzo"
// già usata per gli allegati testo (vedi TextWithTables) — nessuna posizione
// salvata da parser/LLM/Matcher, quindi può non trovare nulla o sbagliare
// con descrizioni duplicate/generiche.
//
// Se trovata, converte la posizione dell'item PDF (spazio pagina, y verso
// l'alto) in coordinate pixel della pagina così come viene mostrata a
// schermo (stessa logica usata internamente dal text layer di pdf.js:
// combinare item.transform con viewport.transform via pdfjsLib.Util).
function findTextMatch(pages, textContents, query, containerWidth) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return null

  for (let p = 0; p < textContents.length; p++) {
    const items = textContents[p]?.items || []
    if (items.length === 0) continue

    let concat = ''
    const offsets = []
    for (const item of items) {
      const start = concat.length
      concat += (item.str || '') + ' '
      offsets.push({ start, end: concat.length, item })
    }

    const idx = concat.toLowerCase().indexOf(q)
    if (idx === -1) continue

    const hit = offsets.find((o) => idx >= o.start && idx < o.end) || offsets[offsets.length - 1]

    try {
      const page = pages[p]
      const baseScale = computeBaseScale(page, containerWidth)
      const viewport = page.getViewport({ scale: baseScale })
      const tx = pdfjsLib.Util.transform(viewport.transform, hit.item.transform)
      const fontHeight = Math.hypot(tx[2], tx[3]) || 12
      const scale = Math.hypot(viewport.transform[0], viewport.transform[1]) || baseScale
      const left = tx[4]
      const width = (hit.item.width || 0) * scale
      // La posizione calcolata dal transform del singolo text item è spesso
      // leggermente disallineata rispetto alla riga reale della tabella nel
      // PDF (non è un vero layout di riga, solo la posizione del glifo) —
      // raddoppiata l'altezza rispetto al solo testo per coprire meglio la
      // riga anche quando il calcolo non è preciso, tenendo però lo stesso
      // centro verticale di prima (cresce sopra e sotto, non solo in giù).
      const baseHeight = fontHeight * 1.15
      const height = baseHeight * 2
      const top = tx[5] - fontHeight - (height - baseHeight) / 2
      return { pageIndex: p, left, top, width, height }
    } catch (e) {
      // Conversione coordinate fallita (item senza transform valido, o API
      // pdf.js cambiata) — degradiamo comunque a "scorri fino a inizio
      // pagina", senza riquadro di evidenziazione.
      console.warn('Impossibile calcolare la posizione del match nel PDF:', e.message)
      return { pageIndex: p, left: 0, top: 0, width: 0, height: 0 }
    }
  }
  return null
}

// Normalizza per il confronto fuzzy sotto — case/spazi non contano, e (bug
// reale trovato 2026-08-13) nemmeno la punteggiatura attaccata alle parole:
// l'evidenza dell'LLM è una frase narrativa ("Riga 1: VETROFRANG...,
// Ref.fornitore:V00911, Qtà 1,000 PZ") con virgole/due punti a fine parola,
// mentre il testo di cella del parser non li ha — con un confronto solo su
// spazi, "v00911," (virgola) e "v00911" (senza) sono due parole DIVERSE per
// il recall sotto, e riga per riga giusta finiva sotto soglia. Sostituendo
// qualunque sequenza di caratteri non alfanumerici con uno spazio prima di
// spezzare in parole, questo problema si risolve — e per estra, spacca
// anche le parole erroneamente incollate senza spazio dall'estrazione del
// parser (es. 'vetrofrang."safe' diventa due parole vere, 'vetrofrang' e
// 'safe', invece di restare una sola stringa che non matcha nulla).
function _normalizeForMatch(s) {
  return (s || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
}

// Punteggio di somiglianza tra il testo di una riga OCR/parser (o riga di
// tabella ricostruita, vedi pdf-parser _extract_page_table_rows) e `query`
// (evidenza LLM) — mai un confronto esatto come findTextMatch. Sottostringa
// in un senso o nell'altro vince sempre (score = rapporto di lunghezza).
// Altrimenti RECALL sulla query: quante delle parole di `query` compaiono
// nella riga candidata, sul totale delle parole di `query` — non un
// rapporto simmetrico sulla dimensione delle due stringhe. Necessario da
// quando le righe di tabella uniscono le celle nell'ordine di colonna (es.
// "E1061A FG002321 ESTINT.POLV.PORTATILE KG 6 ...", con un codice
// secondario "FG002321" incastrato in mezzo, visto su un ordine reale
// 2026-08-11): la query resta comunque un sottoinsieme quasi completo delle
// parole della riga, ma NON una sua sottostringa contigua — un rapporto
// sulla dimensione totale penalizzerebbe ingiustamente la riga giusta
// perche' contiene anche parole extra (gli altri campi della riga tabella).
function _lineMatchScore(lineText, query) {
  const a = _normalizeForMatch(lineText)
  const b = _normalizeForMatch(query)
  if (!a || !b) return 0
  if (a.includes(b) || b.includes(a)) return Math.min(a.length, b.length) / Math.max(a.length, b.length)
  const wordsA = new Set(a.split(' '))
  const wordsB = b.split(' ').filter(Boolean)
  if (!wordsB.length) return 0
  const common = wordsB.filter((w) => wordsA.has(w)).length
  return common / wordsB.length
}

// Soglia scelta in modo prudente: meglio NON evidenziare che evidenziare la
// riga sbagliata. Provvisoria — va calibrata su documenti reali una volta
// che il collega consegna dati OCR veri (vedi nota di progetto).
const LINE_MATCH_THRESHOLD = 0.5

// Fallback a findTextMatch per i documenti senza text-layer nativo (scan, o
// qualunque PDF passato da ocr-module/parser con `metadata.lines`): stessa
// forma di ritorno {pageIndex, left, top, width, height}. Qui lavoriamo
// sempre in FRAZIONI della pagina resa a schermo (origine in alto a
// sinistra, nessun flip come sul text-layer nativo pdf.js), quindi usiamo
// SEMPRE `bbox_relative` (0.0-1.0) quando c'e' — mai `bbox` in quel caso,
// che dopo il fix di ocr-module (2026-08-12, MediaBox reale via pypdf) e'
// in PUNTI PDF ASSOLUTI, un'unita' diversa che qui non serve. `pdf-parser`
// invece non emette `bbox_relative` affatto: il suo `bbox` e' GIA' relativo
// (mai denormalizzato), quindi resta il fallback corretto quando
// `bbox_relative` non c'e'.
function findLineMatch(lines, query, pages, containerWidth) {
  const q = (query || '').trim()
  if (!q || !lines?.length) return null

  let best = null
  let bestScore = 0
  for (const line of lines) {
    const score = _lineMatchScore(line.text, q)
    if (score > bestScore) {
      bestScore = score
      best = line
    }
  }
  if (!best || bestScore < LINE_MATCH_THRESHOLD) return null

  const pageIndex = (best.page || 1) - 1
  const page = pages[pageIndex]
  const bbox = best.bbox_relative || best.bbox
  if (!page || !Array.isArray(bbox) || bbox.length !== 4) return null
  const [x0, y0, x1, y1] = bbox

  const baseScale = computeBaseScale(page, containerWidth)
  const viewport = page.getViewport({ scale: baseScale })
  const left = x0 * viewport.width
  const width = (x1 - x0) * viewport.width
  const rawTop = y0 * viewport.height
  const rawHeight = (y1 - y0) * viewport.height
  // Le righe di tabella (type "table_row") hanno gia' il bbox VERO
  // dell'intera riga logica (pdf-parser _extract_page_table_rows: bbox di
  // row.bbox, non della singola riga di testo) — nessun padding, sarebbe
  // solo rumore. Le righe di testo libero restano alte quanto una riga
  // singola (~1% della pagina, praticamente invisibile a schermo — visto su
  // un caso reale 2026-08-11): stesso trattamento di findTextMatch sopra,
  // raddoppia l'altezza (minimo 14px assoluti) mantenendo lo stesso centro.
  const height = best.type === 'table_row' ? rawHeight : Math.max(rawHeight * 2, 14)
  const top = rawTop - (height - rawHeight) / 2
  return { pageIndex, left, top, width, height }
}

// Fattorizza "prova prima le righe strutturate, poi il text-layer nativo"
// già usato per il singolo riquadro azzurro (hover) — riusato anche per
// calcolare un riquadro per ciascuna riga confermata (verde, N riquadri,
// vedi confirmedTexts). Un `query` vuoto o non trovato restituisce `null`,
// mai un riquadro stantio della query precedente — chi chiama filtra i
// `null` prima di disegnare.
//
// findLineMatch PRIMA, non dopo (invertito 2026-08-14, bug reale): il bbox
// di riga del parser/OCR e' l'intera riga logica, gia' verificato pulito e
// non sovrapposto sui dati reali. findTextMatch invece calcola il box dalla
// posizione/larghezza di un SOLO text item di pdf.js (il primo che contiene
// l'inizio del match nel testo concatenato della pagina) — se quel
// frammento e' corto o vuoto, la larghezza risultante e' vicina a zero e il
// box non supera il filtro `width > 0` al render (righe 680-694 sotto):
// non appare disallineato, non appare affatto. Con findLineMatch provato
// per primo, questo caso non si presenta piu' per i documenti che hanno
// righe strutturate (quasi tutti, native o OCR) — findTextMatch resta solo
// per l'eventuale testo senza una riga corrispondente nell'estrazione.
function computeMatchRect(query, { pages, textContents, lines, containerWidth }) {
  const q = (query || '').trim()
  if (!q || pages.length === 0) return null
  return findLineMatch(lines, q, pages, containerWidth)
    || (textContents.length > 0 && findTextMatch(pages, textContents, q, containerWidth))
    || null
}

/**
 * PDF Viewer — Renderizza le pagine del PDF come canvas a larghezza piena.
 * Usa pdf.js lato client: zero spazio grigio, zero toolbar browser.
 * Le pagine si adattano alla larghezza del container e si scorrono verticalmente.
 *
 * Se non c'è un PDF (pdfBase64 assente) ma c'è testo (allegato non-PDF,
 * es. Excel/mail testuale), mostra il testo grezzo estratto dal parser.
 */
// `onError` (opzionale): banner rosso di sistema in OrderReview.jsx, sopra
// "Recap. ordine" — invocato con `{code, message}` se il caricamento/parsing
// del PDF fallisce, con `null` quando il caricamento riparte o va a buon
// fine (per non lasciare un errore "appeso" da un allegato precedente).
// Il `code` qui è generato lato client (pdf.js non restituisce codici
// stabili) — quando il backend fornirà un vero codice per gli errori di
// allegato, sostituire 'PDF_LOAD_ERROR' con quello.
export default function PdfViewer({
  pdfBase64 = null,
  textContent = null,
  originalFile = null,
  highlightText = null,
  scrollRequest = null,
  onError = null,
  // Righe con bbox per QUESTO allegato (job.attachment_lines[filename], da
  // parser/ocr-module) — usate solo come fallback quando il documento non
  // ha un text-layer nativo leggibile da pdf.js (findTextMatch fallisce),
  // tipicamente uno scan. Vedi findLineMatch sopra.
  lines = null,
  // "ocr" | "native" | undefined — job.attachment_lines_source[filename].
  // Su "ocr" l'evidenziazione e' disabilitata del tutto (anche il percorso
  // nativo findTextMatch, non solo il fallback lines): il bbox su uno scan
  // e' una stima visiva del modello, non una lettura della struttura del
  // PDF — troppo imprecisa per essere utile (richiesta esplicita
  // 2026-08-12). undefined (allegato mai passato da un modulo con questa
  // info, es. non-PDF) si comporta come "native": nessuna disabilitazione.
  linesSource = undefined,
  // Testi (raw_text/description) delle righe ordine attualmente confermate
  // (OrderReview.jsx `onConfirmedLinesChange`) — un riquadro VERDE fisso per
  // ciascuna, N in tutto, disegnato indipendentemente da `highlightText`
  // (quello resta il singolo riquadro AZZURRO temporaneo, solo mentre il
  // puntatore è sopra la riga corrispondente). Richiesta esplicita
  // 2026-08-12: prima esisteva un solo riquadro "fissato" che l'hover su
  // un'altra riga sovrascriveva e non tornava più a mostrare — con un
  // riquadro per riga confermata invece dell'unico "pin", nessun hover può
  // farlo sparire.
  confirmedTexts = [],
  // Ref di un contenitore scrollabile ESTERNO (richiesta 2026-08-13: allegati
  // multipli impilati in un'unica colonna, non più uno alla volta con
  // freccette — vedi Dashboard.jsx/Coda.jsx) — quando presente, questa
  // istanza non scrolla più da sola: lo scroll-to-evidenza e il controllo
  // "è già visibile?" usano QUESTO elemento invece del proprio `.scroller`
  // interno, e `.scroller` perde `position:'relative'` cosi' l'offsetTop
  // delle pagine si accumula fino al vero contenitore scrollabile condiviso
  // (altrimenti si fermerebbe al primo antenato posizionato, cioe' se
  // stesso, e i calcoli di scroll sarebbero relativi al documento sbagliato).
  scrollContainerRef = null,
}) {
  const containerRef = useRef(null)
  const scrollerRef = useRef(null)
  const pageWrapperRefs = useRef([])
  // Ultimo scrollRequest.nonce già "consumato" con uno scroll — evita di
  // rifare lo scroll ad ogni ricalcolo di highlightRect (es. hover che
  // cambia riga senza mai attivare onLineActivate), vedi l'effect più sotto.
  const scrolledNonceRef = useRef(0)
  const [pages, setPages] = useState([])
  const [textContents, setTextContents] = useState([])
  const [highlightRect, setHighlightRect] = useState(null)
  // Un rect per ciascun testo in `confirmedTexts` (stessa forma di
  // highlightRect) — riquadri verdi permanenti, vedi commento sulla prop.
  const [confirmedRects, setConfirmedRects] = useState([])
  const [error, setError] = useState(null)
  // Larghezza di .scroller osservata dal vivo — non solo letta al momento
  // dell'hover: se il pannello cambia larghezza (si trascina il divisore tra
  // documento e form, o si ridimensiona la finestra) mentre un riquadro è già
  // visibile, deve ridimensionarsi/riposizionarsi con lui, non restare
  // congelato alla larghezza di quando è stato calcolato (richiesta esplicita
  // 2026-08-12: "l'area deve rimanere ancorata alla riga ma poter cambiare
  // dimensione"). ResizeObserver invece di un listener su window.resize
  // apposta: il divisore ridimensiona .scroller senza toccare la finestra.
  const [observedWidth, setObservedWidth] = useState(0)
  const resizeObserverRef = useRef(null)
  // Callback ref, non useEffect(..., []): .scroller non esiste ancora al
  // primo render (PdfViewer parte sempre dallo stato "Nessun documento",
  // pdfBase64 è null finché la fetch dell'allegato non risponde — vedi
  // useAttachmentPreview) — un effect con dipendenze vuote gira una sola
  // volta AL MOUNT, trova scrollerRef.current ancora null, e non riprova mai
  // più quando .scroller compare davvero. Risultato osservato (2026-08-12):
  // il riquadro si aggiornava muovendo il mouse (quell'effect legge
  // scrollerRef.current "al volo", non tramite l'observer) ma non
  // trascinando il divisore (l'observer non era mai stato collegato). Una
  // callback ref invece viene richiamata OGNI VOLTA che l'elemento monta o
  // smonta, qualunque sia il motivo — corretto anche se .scroller compare
  // in un render successivo al primo.
  const setScrollerEl = useCallback((el) => {
    scrollerRef.current = el
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect()
      resizeObserverRef.current = null
    }
    if (el) {
      const observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect?.width
        if (width) setObservedWidth(width)
      })
      observer.observe(el)
      resizeObserverRef.current = observer
    }
  }, [])

  useEffect(() => {
    // Azzera sempre un eventuale errore precedente quando si tenta un nuovo
    // caricamento (nuovo allegato, o stesso ma rimontato) — non deve
    // restare visibile un errore di un PDF diverso da quello attuale.
    setError(null)
    onError?.(null)
    // Azzera anche le pagine già renderizzate PRIMA di iniziare a caricare
    // quelle nuove — non dopo (setPages(rendered) più sotto arriva solo a
    // fine parsing, un passaggio asincrono non istantaneo): senza questo
    // reset, cambiando allegato (freccette) il documento PRECEDENTE restava
    // visibile fino al termine del parsing del nuovo, dando l'impressione
    // che "carica lo stesso documento, poi si corregge" (visto 2026-08-12).
    setPages([])
    setTextContents([])
    setHighlightRect(null)
    if (!pdfBase64) return
    let cancelled = false

    async function renderPdf() {
      try {
        // Decodifica base64 → Uint8Array
        const raw = atob(pdfBase64)
        const uint8 = new Uint8Array(raw.length)
        for (let i = 0; i < raw.length; i++) uint8[i] = raw.charCodeAt(i)

        const pdf = await pdfjsLib.getDocument({ data: uint8 }).promise
        if (cancelled) return

        const rendered = []
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i)
          rendered.push(page)
        }
        if (!cancelled) setPages(rendered)

        // Layer testo per lo scorrimento dinamico (fase 2, PDF veri): estratto
        // una volta per pagina dopo il caricamento, non a ogni ricerca — così
        // cambiare riga ordine attiva a destra non ricarica/riparsa il PDF.
        const contents = []
        for (const page of rendered) {
          try {
            contents.push(await page.getTextContent())
          } catch (e) {
            contents.push({ items: [] })
          }
        }
        if (!cancelled) setTextContents(contents)
      } catch (e) {
        console.error('PDF render error:', e)
        if (!cancelled) {
          setError(e.message)
          onError?.({ code: 'PDF_LOAD_ERROR', message: `Impossibile caricare il PDF: ${e.message}` })
        }
      }
    }

    renderPdf()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfBase64])

  // Cerchiatura sui PDF veri: mentre il puntatore è sopra una riga ordine
  // (highlightText, da OrderReview via onLineHover), cerca il testo
  // corrispondente nel PDF già caricato e disegna un riquadro sovrapposto
  // (vedi findTextMatch). Fa ANCHE lo scroll — prima erano due effect
  // separati, uno per il riquadro (dipendeva da highlightText) e uno per lo
  // scroll (dipendeva da scrollRequest.nonce, leggeva highlightRect dallo
  // state). Bug: quando OrderReview chiama onLineHover+onLineActivate
  // insieme nello stesso istante, React aggiorna highlightText e nonce
  // nello STESSO render — l'effect dello scroll leggeva quindi ancora il
  // vecchio highlightRect (quello della riga precedente), calcolato PRIMA
  // che questo effect lo aggiornasse. Unendo i due, lo scroll usa sempre il
  // rect appena calcolato in QUESTA stessa passata, non quello (stantio) del
  // render precedente.
  //
  // Due modalità di scroll (richiesta esplicita 2026-08-12, "scorre in
  // automatico se la riga è su una pagina diversa da quella corrente"):
  // - hover (nessun doppio click): scorre SOLO se il box non è già visibile
  //   nella porzione di documento a schermo — non ad ogni hover, altrimenti
  //   il documento "salterebbe" anche per righe già in vista.
  // - doppio click (scrollRequest.nonce "nuovo", vedi scrolledNonceRef):
  //   scorre SEMPRE, azione esplicita dell'utente, anche se il box è già
  //   visibile (per riportarlo comunque in cima).
  useEffect(() => {
    if (!pdfBase64) return
    if (linesSource === 'ocr' || pages.length === 0) {
      setHighlightRect(null)
      setConfirmedRects([])
      return
    }
    // scrollerRef, non containerRef: il canvas di ogni pagina e' al 100%
    // CSS di .scroller (l'elemento con la scrollbar verticale VERA), non di
    // .container (che non scrolla lui, e quindi non perde la larghezza
    // della scrollbar) — usare containerRef qui introduceva uno scarto
    // sistematico di qualche pixel (la larghezza della scrollbar), piccolo
    // ma visibile su un bbox alto quanto una singola riga di tabella (visto
    // su un ordine reale, 2026-08-11: riquadro "quasi allineato, manca
    // pochissimo"). PageCanvas sotto usa ancora containerRef per la propria
    // scala, ma quella determina solo la RISOLUZIONE del buffer canvas
    // (canvas.width, per la nitidezza), non la dimensione visualizzata —
    // fissata al 100% via CSS indipendentemente da quel calcolo — quindi
    // non e' la stessa fonte di errore e non serve toccarla.
    const containerWidth = observedWidth || scrollerRef.current?.clientWidth || containerRef.current?.clientWidth || 600
    const matchCtx = { pages, textContents, lines, containerWidth }

    // Riquadro azzurro (hover, uno solo, temporaneo).
    const rect = computeMatchRect(highlightText, matchCtx)
    setHighlightRect(rect)

    // Riquadri verdi (uno per riga confermata, permanenti finché resta
    // confermata — vedi confirmedTexts).
    setConfirmedRects(confirmedTexts.map((text) => computeMatchRect(text, matchCtx)).filter(Boolean))

    if (rect) {
      const wrapperEl = pageWrapperRefs.current[rect.pageIndex]
      const scrollerEl = scrollContainerRef?.current || scrollerRef.current
      if (wrapperEl && scrollerEl) {
        const targetTop = wrapperEl.offsetTop + rect.top
        // Doppio click (scrollRequest.nonce "nuovo"): scorre SEMPRE, anche
        // se il box è già visibile — è un'azione esplicita, deve sempre
        // portarlo in cima. Hover (nessun nonce nuovo): scorre SOLO se il
        // box non è già (interamente) visibile nella porzione di documento
        // a schermo — tipicamente perché sta su un'altra pagina (richiesta
        // esplicita 2026-08-12) — altrimenti ogni hover su una riga già
        // visibile farebbe "saltare" il documento senza motivo.
        const explicitScroll = scrollRequest?.nonce && scrollRequest.nonce !== scrolledNonceRef.current
        const MARGIN = 24
        const alreadyVisible = !explicitScroll
          && targetTop >= scrollerEl.scrollTop + MARGIN
          && targetTop + rect.height <= scrollerEl.scrollTop + scrollerEl.clientHeight - MARGIN
        if (explicitScroll) scrolledNonceRef.current = scrollRequest.nonce
        if (explicitScroll || !alreadyVisible) {
          scrollerEl.scrollTo({ top: Math.max(0, targetTop - MARGIN), behavior: 'smooth' })
        }
      }
    }
  }, [highlightText, confirmedTexts, pages, textContents, lines, linesSource, scrollRequest?.nonce, observedWidth])

  if (!pdfBase64 && !textContent) {
    return (
      <div style={s.empty}>
        <FileText size={32} strokeWidth={1.3} color="#B7BCC4" />
        <p style={s.emptyText}>Nessun documento</p>
        <p style={s.emptySubtext}>In attesa dell'allegato originale</p>
      </div>
    )
  }

  // Modalità testo (allegato non-PDF, o mail testuale). Se il testo contiene
  // una tabella in stile markdown (tipico output del parser per Excel/CSV),
  // viene renderizzata come tabella vera invece che come testo grezzo.
  if (!pdfBase64) {
    return (
      <div style={s.container}>
        <OriginalFileBanner file={originalFile} />
        <div style={s.textContent}>
          <TextWithTables text={textContent} highlightQuery={highlightText} scrollNonce={scrollRequest?.nonce} />
        </div>
      </div>
    )
  }

  // Errore pdf.js — fallback iframe
  if (error) {
    const pdfUrl = `data:application/pdf;base64,${pdfBase64}`
    return (
      <div style={s.container}>
        <iframe src={pdfUrl} style={{ border: 'none', width: '100%', height: '100%' }} title="PDF" />
      </div>
    )
  }

  pageWrapperRefs.current = []

  return (
    <div style={s.container} ref={containerRef}>
      {linesSource === 'ocr' && (
        <div style={s.scannedNotice}>
          Evidenziazione non disponibile per i PDF scansionati
        </div>
      )}
      <div
        style={scrollContainerRef ? { ...s.scroller, position: 'static' } : s.scroller}
        ref={setScrollerEl}
      >
        {pages.map((page, i) => (
          <div
            key={i}
            style={s.pageWrapper}
            ref={(el) => { pageWrapperRefs.current[i] = el }}
          >
            <PageCanvas page={page} containerRef={containerRef} />
            {confirmedRects
              .filter((r) => r.pageIndex === i && r.width > 0)
              .map((r, ri) => (
                <div
                  key={ri}
                  style={{
                    ...s.pdfConfirmedHighlight,
                    left: r.left,
                    top: r.top,
                    width: r.width,
                    height: r.height,
                  }}
                />
              ))}
            {highlightRect && highlightRect.pageIndex === i && highlightRect.width > 0 && (
              <div
                style={{
                  ...s.pdfHighlight,
                  left: highlightRect.left,
                  top: highlightRect.top,
                  width: highlightRect.width,
                  height: highlightRect.height,
                }}
              />
            )}
          </div>
        ))}
        {pages.length === 0 && (
          <div style={s.loading}>
            <div className="spinner" style={{ width: 24, height: 24, borderWidth: 2 }} />
            <span style={{ fontSize: '12.5px', color: '#9099A5' }}>Sto leggendo il PDF…</span>
          </div>
        )}
      </div>
    </div>
  )
}


/**
 * Renderizza una singola pagina PDF su un canvas.
 * Il canvas si adatta alla larghezza del container (100%).
 */
function PageCanvas({ page, containerRef }) {
  const canvasRef = useRef(null)
  const rendered = useRef(false)

  useEffect(() => {
    if (!page || !canvasRef.current || rendered.current) return
    rendered.current = true

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')

    // Calcola la scala per riempire la larghezza del container
    const containerWidth = containerRef.current?.clientWidth || 600
    const viewport = page.getViewport({ scale: 1 })
    const baseScale = containerWidth / viewport.width

    // Rendering ad alta qualità: scala 2.5x per testo nitido
    const qualityMultiplier = 2.5
    const renderScale = baseScale * qualityMultiplier
    const scaledViewport = page.getViewport({ scale: renderScale })

    canvas.width = scaledViewport.width
    canvas.height = scaledViewport.height
    canvas.style.width = '100%'
    canvas.style.height = 'auto'

    page.render({
      canvasContext: ctx,
      viewport: scaledViewport,
    })
  }, [page, containerRef])

  return <canvas ref={canvasRef} style={s.canvas} />
}


const s = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    background: '#ffffff',
  },
  scroller: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    background: '#ffffff',
    // position:'relative' rende .scroller l'offsetParent di ogni
    // s.pageWrapper — così wrapperEl.offsetTop (usato per lo scroll dinamico
    // verso il match) è già la distanza dall'inizio del contenuto scrollabile,
    // cioè esattamente il valore da passare a scrollerEl.scrollTo({top}).
    position: 'relative',
  },
  // Contenitore di ogni pagina — position:'relative' per poter sovrapporre
  // il riquadro di evidenziazione (s.pdfHighlight) in coordinate assolute
  // rispetto alla singola pagina, non all'intero documento.
  pageWrapper: {
    position: 'relative',
  },
  canvas: {
    display: 'block',
    width: '100%',
    height: 'auto',
    borderBottom: '1px solid #ECECEE',
  },
  // Riquadro azzurro che cerchia il match trovato nel PDF mentre il
  // puntatore è sopra la riga ordine corrispondente (vedi findTextMatch) —
  // sovrapposto al canvas della pagina, non un vero testo selezionabile (il
  // canvas resta un'immagine), ma sufficiente per indicare visivamente dove
  // guardare. Stesso azzurro (--jenia-violet, #38bdf8) del contorno delle
  // card articolo attive in OrderReview.jsx (lineCardActive) — coerenza
  // richiesta esplicitamente, non più verde. Bordo E riempimento colorati
  // stavolta (non solo bordo come lineCardActive): qui serve individuare
  // un'area precisa nel documento, un riempimento visibile aiuta più che su
  // una card intera.
  pdfHighlight: {
    position: 'absolute',
    background: 'rgba(56,189,248,0.18)',
    border: '2px solid #38bdf8',
    borderRadius: 4,
    pointerEvents: 'none',
  },
  // Riquadro verde permanente per ogni riga ordine confermata (vedi
  // confirmedTexts) — stesso verde della card confermata in OrderReview.jsx
  // (s.lineCardConfirmed, --ok). A differenza di pdfHighlight (azzurro,
  // uno solo, segue l'hover), questi restano finché la riga è confermata,
  // indipendentemente da dove si trova il puntatore.
  pdfConfirmedHighlight: {
    position: 'absolute',
    background: 'rgba(47,125,84,0.16)',
    border: '2px solid #2F7D54',
    borderRadius: 4,
    pointerEvents: 'none',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    height: '100%',
    minHeight: 200,
  },
  textContent: {
    flex: 1,
    overflow: 'auto',
    padding: 16,
    background: '#ffffff',
  },
  downloadBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    background: '#FAFAFB',
    borderBottom: '1px solid #ECECEE',
    color: '#3B6FA8',
    fontSize: '0.8125rem',
    fontWeight: 600,
    textDecoration: 'none',
    flexShrink: 0,
  },
  // Avviso quando linesSource === 'ocr' (PDF scansionato): l'evidenziazione
  // è disabilitata del tutto per questo allegato — bbox stimato da un
  // modello vision, troppo impreciso (richiesta 2026-08-12).
  scannedNotice: {
    flexShrink: 0,
    padding: '8px 16px',
    background: '#FFF7ED',
    borderBottom: '1px solid #FDE4C8',
    color: '#9A5B13',
    fontSize: '0.8125rem',
    fontWeight: 500,
  },
  preText: {
    fontFamily: 'inherit',
    fontSize: '0.875rem',
    lineHeight: 1.6,
    color: '#0A0A0A',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
  },
  // Cerchia la porzione di testo/cella corrispondente alla riga ordine su
  // cui è il puntatore (vedi TextWithTables) — stesso azzurro di
  // pdfHighlight sopra, per coerenza fra i due modi (allegati testuali vs PDF).
  highlight: {
    border: '2px solid #38bdf8',
    background: 'rgba(56,189,248,0.15)',
    borderRadius: 5,
    padding: '1px 3px',
    scrollMarginTop: 24,
    scrollMarginBottom: 24,
  },
  tableWrap: {
    overflowX: 'auto',
    margin: '14px 0',
    border: '1px solid #ECECEE',
    borderRadius: 8,
  },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
    fontSize: '0.8125rem',
  },
  th: {
    textAlign: 'left',
    padding: '7px 12px',
    background: '#FAFAFB',
    borderBottom: '1px solid #E1E1E4',
    fontWeight: 600,
    color: '#0A0A0A',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '7px 12px',
    borderTop: '1px solid #ECECEE',
    color: '#0A0A0A',
  },
  empty: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    background: '#FAFAFB',
  },
  emptyText: {
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: '#525B67',
  },
  emptySubtext: {
    fontSize: '0.8125rem',
    color: '#9099A5',
  },
}
